<template>
  <v-dialog
    :value="open"
    max-width="640"
    overlay-color="neutral lighten-1"
    :persistent="checkoutInProgress"
    @input="emitClose"
  >
    <v-card class="subscription-change-modal">
      <v-card-title class="justify-space-between align-center">
        <div>
          <h4 class="text-h4 mb-1">
            {{ heading }}
          </h4>
        </div>

        <v-btn
          icon
          :disabled="checkoutInProgress"
          :ripple="false"
          data-test="subscription-change-modal-close-icon"
          @click="emitClose"
        >
          <v-icon>$close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="subscription-change-modal__body">
        <slot />

        <Alert
          v-if="showDowngradeInfo && !showModalContentLoader"
          dense
          outlined
          type="warning"
          icon="warning-system"
          regular-title
          class="mb-4"
        >
          <template #title>
            <span v-html="downgradeInfoText" />
          </template>
        </Alert>

        <Alert
          v-if="showUpgradeInfo && !showModalContentLoader"
          dense
          outlined
          type="info"
          icon="info-system"
          regular-title
          class="mb-4"
        >
          <template #title>
            <span>{{ $t('fields.checkout.subscription_change_modal.upgrade_info') }}</span>
          </template>
        </Alert>

        <div class="subscription-change-modal__summary">
          <div v-if="showModalContentLoader" class="subscription-change-modal__loader">
            <v-progress-circular indeterminate color="primary" size="32" width="3" />
          </div>
          <div v-else>
            <Alert
              v-if="checkoutError"
              dense
              outlined
              type="error"
              icon="forbidden-system"
              regular-title
              class="mb-4"
              title-class="w-100"
            >
              <template #title>
                <div>
                  <span>{{ $t('fields.checkout.subscription_change_modal.payment_error') }}</span>
                  <div class="text-right mt-2">
                    <v-btn
                      text
                      :ripple="false"
                      color="primary"
                      small
                      class="v-btn--text--link"
                      :to="{ path: '/my-account/billing', query: { openModal: 'paymentEdit' } }"
                    >
                      <span class="navy--text text--darken-1">
                        {{ $t('fields.checkout.subscription_change_modal.payment_error_cta') }}
                      </span>
                    </v-btn>
                  </div>
                </div>
              </template>
            </Alert>

            <Alert
              v-if="taxCalculationError"
              dense
              outlined
              type="error"
              icon="forbidden-system"
              regular-title
              class="mb-4"
              data-test="subscription-change-modal-tax-error"
            >
              <template #title>
                <span>{{ $t('fields.checkout.form.tax_calculation_failed') }}</span>
              </template>
            </Alert>

            <OrderSummary
              v-if="plan"
              :selected-plan="plan"
              :stripe-checkout-service="stripeCheckoutService"
              :checkout-type="checkoutType"
              @pricePlanChanged="onPricePlanChanged"
            />

            <div class="subscription-change-modal__tos mt-6">
              <CheckoutTosCheckbox
                v-model="tosConfirmed"
                checkbox-id="subscription-change-modal-tos"
                :disabled="showModalContentLoader || checkoutInProgress"
                :show-error="showTosError"
              />
            </div>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="pa-6">
        <v-spacer />
        <v-btn
          outlined
          rounded
          :ripple="false"
          :disabled="checkoutInProgress"
          color="primary"
          large
          class="mr-4"
          data-test="subscription-change-modal-cancel"
          @click="emitClose"
        >
          {{ closeLabel }}
        </v-btn>
        <v-btn
          depressed
          rounded
          :ripple="false"
          color="primary"
          large
          :loading="checkoutInProgress"
          :disabled="showModalContentLoader || checkoutInProgress"
          data-test="subscription-change-modal-checkout"
          @click="onCheckoutNow"
        >
          {{ $t('fields.checkout.subscription_change_modal.confirm_button') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { planTitleKey } from '@/Services/Subscription/PlanViewService';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Plan, Subscription } from '@/classes/stripe';
import OrderSummary from '@/components/PaymentForm/OrderSummary.vue';
import Alert from '@/components/Alert.vue';
import CheckoutTosCheckbox from '@/components/CheckoutTosCheckbox.vue';
import StripeCheckoutService from '@/Services/Subscription/StripeCheckoutService';
import StripeService from '@/Services/Subscription/StripeService';
import StripeSubscriptionService from '@/Services/Subscription/StripeSubscriptionService';
import Authenticator from '@/Services/Auth/Authenticator';
import store from '@/store';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import AppConfig from '@/AppConfig';

// Error code returned by checkout-api when a downgrade target can't hold the account's active dynamic codes.
const CODE_LIMIT_EXCEEDED_ERROR = 'downgrade_code_limit_exceeded';

@Component({
  components: {
    OrderSummary,
    Alert,
    CheckoutTosCheckbox,
  },
})
export default class SubscriptionChangeModal extends Vue {
  @Prop({ type: Boolean, default: false })
  readonly open!: boolean;

  @Prop({ type: Object, default: null })
  readonly plan!: Plan | null;

  @Prop({ type: String, default: null })
  readonly intent!: 'upgrade' | 'downgrade' | null;

  @Prop({ type: String, default: null })
  readonly planId!: string | null;

  stripeCheckoutService: StripeCheckoutService = new StripeCheckoutService();
  stripeService: StripeService | null = null;
  stripeSubscriptionService: StripeSubscriptionService = new StripeSubscriptionService();
  summaryLoading = false;
  summaryReady = false;
  tosConfirmed = false;
  displayTosError = false;
  checkoutInProgress = false;
  checkoutError = false;
  taxCalculationError = false;
  private lastRequestedPlanId: string | null = null;

  /**
   * Label shown at the top of the modal based on intent.
   */
  get heading(): string {
    const planTitle = this.plan ? this.$t(planTitleKey(this.plan)).toString() : '';

    if (this.intent === 'upgrade') {
      return this.$t('fields.checkout.header_upgrade_plan', {
        plan_title: planTitle,
      }).toString();
    }
    if (this.intent === 'downgrade') {
      return this.$t('fields.checkout.header_downgrade_plan', {
        plan_title: planTitle,
      }).toString();
    }
    return planTitle || '';
  }

  /**
   * Text for the primary close button.
   */
  get closeLabel(): string {
    return this.$t('fields.downgrade_restricted_modal.cancel').toString();
  }

  /**
   * Determines whether summary behaves like upgrade/downgrade.
   */
  get checkoutType(): string {
    if (!this.intent) {
      throw new Error('SubscriptionChangeModal requires an intent.');
    }
    return this.intent;
  }

  /**
   * Currently active subscription from the store.
   */
  get activeSubscription(): Subscription {
    return store.state.subscription.activeSubscription;
  }

  /**
   * Translation key for the plan that is currently active.
   */
  get currentPlanTranslationKey(): string | undefined {
    return store.state.subscription.plans.find((plan: Plan) => plan.id === this.activeSubscription?.plan_id)
      ?.translationKey;
  }

  /**
   * Formatted date when the current plan ends.
   */
  get currentPlanEndDate(): string | null {
    if (!this.activeSubscription?.current_period_end) {
      return null;
    }
    return this.activeSubscription.current_period_end.format('MMMM D, YYYY');
  }

  /**
   * Title of the newly selected plan.
   */
  get selectedPlanTitle(): string {
    if (!this.plan) {
      return '';
    }

    return this.$t(planTitleKey(this.plan)).toString();
  }

  /**
   * Whether the downgrade info alert should be displayed.
   */
  get showDowngradeInfo(): boolean {
    return this.intent === 'downgrade' && !!this.plan && !!this.currentPlanTranslationKey && !!this.currentPlanEndDate;
  }

  /**
   * Copy shown inside the downgrade info alert.
   */
  get downgradeInfoText(): string {
    if (!this.showDowngradeInfo) {
      return '';
    }

    return this.$t('fields.checkout.header_downgrade_plan_info', {
      current_plan_title: this.$t(`fields.${this.currentPlanTranslationKey}`).toString(),
      expire_date: this.currentPlanEndDate,
      new_plan_title: this.selectedPlanTitle,
    }).toString();
  }

  /**
   * Whether the upgrade info alert should be displayed.
   */
  get showUpgradeInfo(): boolean {
    return this.intent === 'upgrade';
  }

  /**
   * Whether the TOS helper text should be shown.
   */
  get showTosError(): boolean {
    return !this.tosConfirmed && this.displayTosError;
  }

  /**
   * Spinner should show whenever checkout data is not ready.
   */
  get showModalContentLoader(): boolean {
    return this.summaryLoading || !this.summaryReady;
  }

  /**
   * When dialog opens, ensure summary prerequisites are loaded.
   */
  @Watch('open')
  async onOpenChanged(isOpen: boolean): Promise<void> {
    if (isOpen) {
      await this.prepareSummary();
    } else {
      this.summaryLoading = false;
    }
  }

  /**
   * Attempt auto-open when a deep-linked plan is provided.
   */
  @Watch('planId', { immediate: true })
  onPlanIdChanged(nextPlanId: string | null): void {
    if (nextPlanId) {
      void this.openSubscriptionChangeModal(nextPlanId);
    }
  }

  /**
   * Initialize Stripe checkout data and VAT rates.
   */
  async prepareSummary(): Promise<void> {
    if (this.summaryReady) {
      return;
    }
    this.summaryLoading = true;
    try {
      // Refresh the team so a billing address saved earlier this session (e.g. during the
      // initial purchase) is present before we read it. Without this the modal reads a stale
      // cached team, initCustomerData copies an empty postal_code, and detectVatRate falls back
      // to tax_exempt='' which hides the prorated-discount row until a full page reload.
      await Authenticator.fetchUser();
      this.stripeCheckoutService.initCustomerData();
      await this.stripeCheckoutService.updateCustomerVatRate();

      // Initialize StripeService for 3DS handling (constructor calls initStripe() internally)
      const country =
        this.stripeCheckoutService.customer?.address?.country || this.stripeCheckoutService.location.country_iso2;
      if (country === '') {
        this.checkoutError = true;
        this.summaryReady = true;
        return;
      }
      this.stripeService = new StripeService(country);

      this.summaryReady = true;
    } catch (error) {
      Sentry.captureException(error);
      this.checkoutError = true;
      this.summaryReady = true;
    } finally {
      this.summaryLoading = false;
    }
  }

  /**
   * Bubble plan changes to parent components.
   */
  onPricePlanChanged(plan: Plan): void {
    this.$emit('plan-change', plan);
  }

  /**
   * Close dialog by emitting cancel event.
   */
  emitClose(): void {
    this.checkoutError = false;
    this.taxCalculationError = false;
    this.displayTosError = false;
    this.tosConfirmed = false;
    this.$emit('cancel');
  }

  /**
   * Handles checkout process, including API call and 3DS authentication.
   */
  async onCheckoutNow(): Promise<void> {
    this.displayTosError = true;
    if (!this.tosConfirmed) {
      return;
    }

    const targetPlan = this.plan;
    if (!targetPlan) {
      return;
    }

    // Never charge without a tax amount confirmed by the tax service
    // (blocks while loadingVatRate is true, not only after taxLookupFailed).
    if (!this.stripeCheckoutService.isTaxConfirmed) {
      this.taxCalculationError = true;
      return;
    }

    this.taxCalculationError = false;
    this.checkoutInProgress = true;

    this.showFullScreenLoader();
    try {
      const response = await axios.patch(`${AppConfig.getAPIBaseUrl()}/subscription/stripe/${targetPlan.id}`, {
        plan_id: targetPlan.id,
      });

      if (!response?.data || response.data.error) {
        this.checkoutError = true;
        return;
      }

      // Payment succeeded or doesn't require confirmation
      const planQuery = targetPlan.planName ? targetPlan.planName.toLowerCase() : targetPlan.id;
      await this.$router.push({
        name: 'checkout_payment_complete',
        params: { type: this.intent ?? 'upgrade' },
        query: { plan: planQuery },
      });
    } catch (error) {
      if (this.isCodeLimitExceededError(error)) {
        // The backend rejected the downgrade because the account has too many active dynamic codes.
        // Hand over to the parent, which shows the "pause codes first" modal.
        this.emitClose();
        this.$emit('code-limit-exceeded', targetPlan);
        return;
      }

      Sentry.captureException(error);
      this.checkoutError = true;
    } finally {
      this.hideFullScreenLoader();
      this.checkoutInProgress = false;
    }
  }

  /**
   * True when the API refused a downgrade because the account has more active dynamic codes than the new plan allows.
   */
  isCodeLimitExceededError(error: unknown): boolean {
    const response = (error as { response?: { status?: number; data?: { error?: string } } })?.response;
    return response?.status === 422 && response?.data?.error === CODE_LIMIT_EXCEEDED_ERROR;
  }

  /**
   * Shows the same full-screen loader used on the checkout page.
   */
  showFullScreenLoader(): void {
    this.$store.commit('layout/toggleGlobalLoader', true);
    this.$store.commit('layout/toggleGlobalOverlay', true);
  }

  /**
   * Removes the full-screen loader if it was injected.
   */
  hideFullScreenLoader(): void {
    this.$store.commit('layout/toggleGlobalLoader', false);
    this.$store.commit('layout/toggleGlobalOverlay', false);
  }

  /**
   * Attempt to open the modal automatically for a deep-linked plan.
   */
  private async openSubscriptionChangeModal(planId: string): Promise<void> {
    // Avoid reopening the modal for the exact same plan repeatedly
    if (planId === this.lastRequestedPlanId) {
      return;
    }

    try {
      await this.ensureAutoOpenDataReady();
      const planExists = store.state.subscription.plans.find((candidate: Plan) => candidate.id === planId);
      if (!planExists) {
        return;
      }

      const isUpgrade = this.stripeSubscriptionService.isPlanSuperior(planId);
      const isDowngrade = this.stripeSubscriptionService.isPlanInferior(planId);

      if (isUpgrade === true || isDowngrade === true) {
        const intent = isUpgrade ? 'upgrade' : 'downgrade';
        this.lastRequestedPlanId = planId;
        this.$emit('open', { plan: planExists, intent });
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }

  /**
   * Ensures the subscription store contains the required data before auto-opening.
   */
  private async ensureAutoOpenDataReady(): Promise<void> {
    if (!store.state.subscription.plans?.length) {
      await store.dispatch('subscription/initSubscriptionPlans');
    }

    if (!store.state.subscription.activeSubscription?.id) {
      await store.dispatch('subscription/loadSubscription');
    }

    await this.stripeSubscriptionService.initSubscription();
  }
}
</script>

<style lang="scss" scoped>
.subscription-change-modal {
  &__body {
    min-height: 160px;
    display: block;
    padding: 0 24px 24px;
  }

  &__summary {
    width: 100%;
  }

  &__loader {
    min-height: 160px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
